/**
 * FollowupCallModal
 * ─────────────────────────────────────────────────────────────────────────────
 * Mid-test voice interview the candidate completes IMMEDIATELY AFTER submitting
 * each coding question. Verifies they actually wrote the code by asking 2–3
 * short questions about it via an OpenAI Realtime AI agent.
 *
 * Behavioural contract (the "hard" parts):
 *
 *   • Mic is REQUIRED. If the candidate denies mic permission, has no audio
 *     device, or the browser blocks the request, we render a hard-stop screen
 *     with retry. There is NO bypass — they cannot reach the next coding
 *     question until they grant mic. This is intentional: the recruiter
 *     toggled this feature on as an anti-cheat measure.
 *
 *   • If the backend says `skip: true` (feature flag disabled at flip-time,
 *     or some required input missing), we close the modal IMMEDIATELY without
 *     any UI and call onComplete with skip=true. The parent (iCode.jsx) then
 *     unlocks the next coding question with no follow-up evidence captured.
 *
 *   • If the OpenAI Realtime session fails mid-call (network drop, 5xx, etc.),
 *     we still POST a partial events payload to the backend so the recruiter
 *     dashboard has SOMETHING to show — preventing "silent skip" via a forced
 *     network failure.
 *
 *   • Hard wall-clock timer (max_seconds, default 180s). When it hits, we
 *     send whatever function-call events we've captured + close the session.
 *
 * Props
 *   open                Boolean — opens/closes the modal.
 *   codingQuestion      String — the original problem statement (sent to the AI).
 *   candidateCode       String — what the candidate just submitted.
 *   language            String — "Python" | "C++" | "C" | "Java" | "JavaScript" | "NodeJS".
 *   testId              String — the coding test id; links the events back to
 *                       code_fingerprints in the backend.
 *   questionIdx         Number — 0-indexed coding question position (matches
 *                       code_fingerprints.question_idx). Displayed as +1.
 *   totalQuestions      Number — total coding questions in this test.
 *   candidateFirstName  String? — used in the AI's greeting.
 *   onComplete({ stored, skipped, micGranted, micDeniedReason })
 *                       Called EXACTLY ONCE when the modal is ready to close.
 *                       Parent unlocks the next coding question in response.
 *
 * Pattern reference: cloned from Web-Interviewer/frontend/src/Participant/
 * ScreeningInterview.jsx (GA `/v1/realtime/calls` SDP flow) with three
 * critical additions the reference lacks:
 *   1. Function-call branch in dc.onmessage (record_question / record_answer
 *      / end_session) — the reference parses transcript events only.
 *   2. Explicit getUserMedia rejection UX — the reference swallows the
 *      error silently.
 *   3. Clean teardown of the audio element (borrowed from RealTimeInterview.jsx).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Mic, MicOff, AlertCircle, CheckCircle2, Loader2, Clock,
  RefreshCw, X, MessageSquare,
} from 'lucide-react';
// API paths are relative so the bundled SPA hits the same origin that
// served it — matches the rest of User/Coding/* which also uses bare
// "/api/..." paths (see iCode.jsx fetch sites). The Vite dev server
// proxies these to the Flask backend; in prod they go through nginx.
const API_BASE = "";


// ─── Constants ───────────────────────────────────────────────────────────────

const REALTIME_SDP_URL = 'https://api.openai.com/v1/realtime/calls';
const SDP_TIMEOUT_MS   = 30_000;

// Internal phases of the modal. Translates to which sub-view we render.
const PHASE = {
  REQUESTING_MIC:    'requesting_mic',
  MIC_DENIED:        'mic_denied',         // hard-stop, only Retry button
  FETCHING_SESSION:  'fetching_session',   // POST /api/coding/start_followup
  CONNECTING:        'connecting',         // SDP exchange in flight
  ACTIVE:            'active',             // AI is speaking / listening
  ENDED:             'ended',              // AI called end_session OR timeout
  SKIPPED:           'skipped',            // feature flag off → close immediately
  ERROR:             'error',              // unrecoverable error path
};


// ─── Component ───────────────────────────────────────────────────────────────

export default function FollowupCallModal({
  open,
  codingQuestion,
  candidateCode,
  language,
  testId,
  questionIdx,
  totalQuestions,
  candidateFirstName,
  originalBoilerplate,   // snapshot of what was in the editor when Q first loaded
  onComplete,
}) {
  // Phase — drives which sub-view renders.
  const [phase, setPhase]         = useState(PHASE.REQUESTING_MIC);
  // Human-readable error message for PHASE.ERROR / PHASE.MIC_DENIED.
  const [errorMessage, setErrorMessage] = useState('');
  // Live transcript (deltas accumulate per response.id; user-turn lines
  // are pushed when a final input_audio_transcription.completed event arrives).
  const [transcript, setTranscript] = useState([]);   // [{role, text}]
  // The AI's current "is speaking" indicator (drives the mic pulse animation).
  const [aiSpeaking, setAiSpeaking] = useState(false);
  // Countdown to the hard wall.
  const [secondsLeft, setSecondsLeft] = useState(180);

  // ─── Refs — anything we don't render but must clean up on unmount ─────────

  const pcRef           = useRef(null);
  const dcRef           = useRef(null);
  const audioElRef      = useRef(null);
  const localStreamRef  = useRef(null);
  const ephemeralKeyRef = useRef(null);
  const sessionIdRef    = useRef(null);
  const startedAtRef    = useRef(null);
  const timerIdRef      = useRef(null);

  // Per-session event accumulator. Each `record_question` / `record_answer`
  // / `end_session` function call from the AI lands here as an `{name, args}`
  // entry. Flushed to the backend in one POST when the call ends.
  const eventsRef       = useRef([]);

  // Mic status — written when we successfully grant or fail to grant the
  // mic; included in the final POST so the recruiter dashboard can show
  // "mic denied" as a verdict signal even if no AI events were captured.
  const micGrantedRef        = useRef(false);
  const micDeniedReasonRef   = useRef(null);

  // Guard so onComplete fires EXACTLY ONCE — closing the modal from
  // multiple paths (AI end_session, timeout, X button, unmount) all funnel
  // through finish().
  const completedRef = useRef(false);

  // Snapshot of strict-mode unmount-safety: we don't want a fetch resolving
  // after the user closed the modal to flip state on a dead component.
  const isMountedRef = useRef(true);


  // ─────────────────────────────────────────────────────────────────────────
  // Lifecycle: kick off the whole flow when `open` flips true.
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!open) return;
    isMountedRef.current = true;
    completedRef.current = false;
    eventsRef.current = [];
    setPhase(PHASE.REQUESTING_MIC);
    setErrorMessage('');
    setTranscript([]);
    setSecondsLeft(180);
    micGrantedRef.current = false;
    micDeniedReasonRef.current = null;

    requestMicThenStart();

    return () => {
      isMountedRef.current = false;
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  // Countdown ticker — only runs in ACTIVE phase. Fires finish('timeout')
  // when it hits 0 so the recruiter still sees what the AI captured up
  // to that point.
  useEffect(() => {
    if (phase !== PHASE.ACTIVE) {
      if (timerIdRef.current) { clearInterval(timerIdRef.current); timerIdRef.current = null; }
      return;
    }
    timerIdRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerIdRef.current);
          timerIdRef.current = null;
          finish('timeout');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (timerIdRef.current) { clearInterval(timerIdRef.current); timerIdRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);


  // ─────────────────────────────────────────────────────────────────────────
  // Step 1 — request mic permission BEFORE any backend call.
  // We do it first so a denied mic results in zero load on the backend
  // (no wasted Realtime session minted).
  //
  // Optimisation: the candidate already granted mic in the pre-test
  // System Check (TestingSection.jsx). The browser remembers the grant
  // for the session, so the getUserMedia() call below will normally
  // resolve in <100 ms with no popup. We fast-path that case by
  // skipping straight from REQUESTING_MIC → FETCHING_SESSION without a
  // visible flash. The denial paths (mid-test mic loss, app stole the
  // device, etc.) still surface their full hard-stop UX.
  // ─────────────────────────────────────────────────────────────────────────

  const requestMicThenStart = useCallback(async () => {
    try {
      // Best-effort: enumerate devices first to detect "no mic at all"
      // before we trigger the permission prompt. Cheap on grants we
      // already have; falls through gracefully otherwise.
      try {
        if (navigator.mediaDevices?.enumerateDevices) {
          const devs = await navigator.mediaDevices.enumerateDevices();
          const audioInputs = devs.filter((d) => d.kind === 'audioinput');
          if (audioInputs.length === 0) {
            throw Object.assign(new Error('No audio input device'), { name: 'NotFoundError' });
          }
        }
      } catch (enumErr) {
        if (enumErr?.name === 'NotFoundError') throw enumErr;
        // For any other enumerate failure, proceed to getUserMedia which
        // will surface the real error (permissions API differs by browser).
      }

      const ms = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!isMountedRef.current) {
        // User closed the modal while the permission prompt was up.
        ms.getTracks().forEach((t) => t.stop());
        return;
      }
      localStreamRef.current = ms;
      micGrantedRef.current = true;
      micDeniedReasonRef.current = null;
      setPhase(PHASE.FETCHING_SESSION);
      await startSession();
    } catch (err) {
      // The reference ScreeningInterview.jsx swallows this — we MUST surface
      // it so the candidate knows why they can't proceed.
      const name = err && err.name ? String(err.name) : 'Unknown';
      let reason = 'user_denied';
      let humanMsg = 'Microphone access was denied. You must grant microphone permission to continue with this test.';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        reason = 'user_denied';
        humanMsg = 'You denied microphone access. Click "Try Again" below, then grant microphone permission in your browser. This test cannot continue without it.';
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError') {
        reason = 'no_device';
        humanMsg = 'No microphone was detected on your device. Plug in a microphone or headset, then click "Try Again".';
      } else if (name === 'NotReadableError') {
        reason = 'device_busy';
        humanMsg = 'Your microphone is being used by another application. Close other apps using the mic (Zoom, Meet, Discord) and click "Try Again".';
      } else {
        reason = 'browser_unsupported';
        humanMsg = `Your browser blocked microphone access (${name}). Try a different browser (Chrome / Firefox / Edge) and reload this page.`;
      }
      micGrantedRef.current = false;
      micDeniedReasonRef.current = reason;
      if (isMountedRef.current) {
        setErrorMessage(humanMsg);
        setPhase(PHASE.MIC_DENIED);
      }
    }
  }, []);


  // ─────────────────────────────────────────────────────────────────────────
  // Step 2 — POST /api/coding/start_followup to get an ephemeral key.
  // ─────────────────────────────────────────────────────────────────────────

  const startSession = useCallback(async () => {
    try {
      const url = `${API_BASE}/api/coding/start_followup`;
      const resp = await fetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          test_id:                testId,
          question_idx:           questionIdx,
          question_text:          codingQuestion,
          candidate_code:         candidateCode,
          language:               language,
          total_questions:        totalQuestions,
          candidate_first_name:   candidateFirstName || undefined,
          // When non-empty, the backend runs attempt_detector.was_attempted()
          // and returns {skip: true, reason: "not_attempted"} for
          // submissions that are essentially the unmodified boilerplate.
          // Safe fallback: backend treats null/empty as "client doesn't
          // know" and proceeds with the session.
          original_boilerplate:   originalBoilerplate || undefined,
        }),
      });
      if (!isMountedRef.current) return;

      const data = await resp.json().catch(() => ({}));

      if (data.skip) {
        // Feature flag was flipped off OR inputs invalid. Close cleanly —
        // candidate proceeds with NO follow-up evidence captured.
        setPhase(PHASE.SKIPPED);
        finishSkip();
        return;
      }
      if (!resp.ok) {
        throw new Error(data.error || `start_followup returned ${resp.status}`);
      }

      sessionIdRef.current = data.session_id || null;
      startedAtRef.current = new Date().toISOString();
      setSecondsLeft(Number(data.max_seconds) || 180);

      // Extract the ephemeral key from the OpenAI session payload. Mirrors
      // ScreeningInterview's defensive 5-path lookup since OpenAI has
      // shipped the key in slightly different fields across versions.
      const s = data.openai_session || {};
      const key =
        s?.client_secret?.value ??
        s?.client_secret ??
        s?.value ??
        s?.token ??
        null;
      if (!key) {
        throw new Error('No ephemeral key in /api/coding/start_followup response');
      }
      ephemeralKeyRef.current = key;

      setPhase(PHASE.CONNECTING);
      await initializeRealtime();
    } catch (err) {
      console.error('[followup] startSession failed:', err);
      if (!isMountedRef.current) return;
      setErrorMessage(
        'We couldn\'t start the verification call. The test can continue without it — click Close to move on.',
      );
      setPhase(PHASE.ERROR);
    }
  }, [
    testId, questionIdx, codingQuestion, candidateCode, language,
    totalQuestions, candidateFirstName,
  ]);


  // ─────────────────────────────────────────────────────────────────────────
  // Step 3 — open WebRTC to OpenAI Realtime and wire all event listeners.
  // ─────────────────────────────────────────────────────────────────────────

  const initializeRealtime = useCallback(async () => {
    try {
      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      pcRef.current = pc;

      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioEl.muted    = false;
      audioElRef.current = audioEl;
      audioEl.addEventListener('canplay', () => { audioEl.play().catch(() => {}); });
      audioEl.addEventListener('play',  () => { setAiSpeaking(true); });
      audioEl.addEventListener('ended', () => { setAiSpeaking(false); });

      pc.ontrack = (ev) => {
        if (ev.streams && ev.streams[0]) {
          audioEl.srcObject = ev.streams[0];
        }
      };

      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.onmessage = handleDataChannelMessage;
      dc.onopen    = () => {
        // OpenAI Realtime defaults to "listen first, respond when the user
        // pauses" — so without an explicit kick, the AI sits silent until
        // the candidate speaks. That's wrong for our flow: the AI should
        // greet + ask the first question THE MOMENT the call connects,
        // matching the "Quick check on your code…" opening in our system
        // prompt (services/followup/prompt_builder.py).
        //
        // The fix is a single `response.create` event. We don't need to
        // pass instructions or modalities — those are already baked into
        // the session config we sent when minting the ephemeral key.
        try {
          dc.send(JSON.stringify({ type: 'response.create' }));
          console.log('[followup] sent initial response.create — AI should greet now');
        } catch (e) {
          // Vanishingly rare: the channel opens but immediately errors.
          // The AI will eventually start on its own when the candidate
          // speaks, so this is non-fatal.
          console.warn('[followup] initial response.create failed', e);
        }
      };
      dc.onerror   = (e) => { console.warn('[followup] dc error', e); };

      // Add mic tracks BEFORE creating the offer.
      const ms = localStreamRef.current;
      if (ms) {
        for (const t of ms.getTracks()) pc.addTrack(t, ms);
      }

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpResp = await fetch(REALTIME_SDP_URL, {
        method:  'POST',
        headers: {
          Authorization:  `Bearer ${ephemeralKeyRef.current}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
        signal: AbortSignal.timeout(SDP_TIMEOUT_MS),
      });
      if (!sdpResp.ok) {
        const body = await sdpResp.text().catch(() => '');
        throw new Error(`Realtime SDP exchange ${sdpResp.status}: ${body.slice(0, 200)}`);
      }
      const answer = await sdpResp.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });

      if (isMountedRef.current) {
        setPhase(PHASE.ACTIVE);
      }
    } catch (err) {
      console.error('[followup] initializeRealtime failed:', err);
      if (!isMountedRef.current) return;
      setErrorMessage(
        'The verification call couldn\'t connect. The test can continue without it — click Close to move on.',
      );
      setPhase(PHASE.ERROR);
    }
  }, []);


  // ─────────────────────────────────────────────────────────────────────────
  // Data-channel handler — captures function calls + maintains the transcript.
  // ─────────────────────────────────────────────────────────────────────────

  const handleDataChannelMessage = useCallback((ev) => {
    let data;
    try {
      data = JSON.parse(ev.data.toString());
    } catch (e) {
      console.warn('[followup] non-JSON dc message ignored', ev.data);
      return;
    }
    const type = String(data?.type || '');

    // 1. Function calls — the load-bearing path. The AI calls our 3 tools
    //    here. We record the args, send a function_call_output back so the
    //    model continues, and (for end_session) finish the call.
    if (type === 'response.function_call_arguments.done') {
      const name   = data.name;
      const callId = data.call_id;
      let args = {};
      try { args = JSON.parse(data.arguments || '{}'); } catch (_) { args = {}; }

      eventsRef.current.push({ name, args });

      // Acknowledge the function call so the model resumes its turn.
      try {
        dcRef.current?.send(JSON.stringify({
          type: 'conversation.item.create',
          item: {
            type:    'function_call_output',
            call_id: callId,
            output:  JSON.stringify({ ok: true }),
          },
        }));
        dcRef.current?.send(JSON.stringify({ type: 'response.create' }));
      } catch (e) {
        console.warn('[followup] failed to ACK function call', e);
      }

      if (name === 'end_session') {
        finish('ai_ended');
      }
      return;
    }

    // 2. AI speech transcript deltas — for the live transcript UI.
    if (type === 'response.audio_transcript.delta' && data.delta) {
      setTranscript((prev) => {
        // Append to the last assistant line if there is one — otherwise
        // start a new assistant line.
        if (prev.length && prev[prev.length - 1].role === 'assistant') {
          const copy = prev.slice();
          copy[copy.length - 1] = {
            ...copy[copy.length - 1],
            text: copy[copy.length - 1].text + data.delta,
          };
          return copy;
        }
        return [...prev, { role: 'assistant', text: String(data.delta) }];
      });
      return;
    }

    // 3. Candidate speech transcribed.
    if (type === 'conversation.item.input_audio_transcription.completed' && data.transcript) {
      setTranscript((prev) => [...prev, { role: 'user', text: String(data.transcript) }]);
      return;
    }

    // 4. Errors from OpenAI — useful breadcrumbs in the console; never
    //    surface mid-call to the candidate.
    if (type === 'error' || data?.error) {
      console.warn('[followup] realtime error', data);
      return;
    }
  }, []);


  // ─────────────────────────────────────────────────────────────────────────
  // finish() — single funnel for "we're done"
  // Reasons:
  //   - "ai_ended"  : AI called end_session normally
  //   - "timeout"   : wall-clock hit zero
  //   - "user_close": user clicked X (allowed only after ACTIVE phase)
  //   - "error"     : recoverable error path post-connect
  // ─────────────────────────────────────────────────────────────────────────

  const finish = useCallback(async (reason) => {
    if (completedRef.current) return;
    completedRef.current = true;

    const durationS = startedAtRef.current
      ? Math.max(0, Math.round((Date.now() - new Date(startedAtRef.current).getTime()) / 1000))
      : 0;

    // Stop talking to OpenAI ASAP so we don't get billed for extra tokens
    // while the POST below is in flight.
    teardown();

    // Best-effort POST to backend. We hit it even when reason !== "ai_ended"
    // so the dashboard never has a "session opened but no record" mystery.
    try {
      const url = `${API_BASE}/api/coding/followup_event`;
      const payload = {
        test_id:           testId,
        question_idx:      questionIdx,
        session_id:        sessionIdRef.current,
        events:            eventsRef.current,
        duration_s:        durationS,
        transcript:        transcript.slice(-50),    // tail only
        mic_granted:       micGrantedRef.current,
        mic_denied_reason: micDeniedReasonRef.current,
        started_at:        startedAtRef.current,
        end_reason:        reason,
      };
      const resp = await fetch(url, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const body = await resp.json().catch(() => ({}));
      if (isMountedRef.current) {
        setPhase(PHASE.ENDED);
      }
      onComplete?.({
        stored:           !!body?.stored,
        skipped:          false,
        micGranted:       micGrantedRef.current,
        micDeniedReason:  micDeniedReasonRef.current,
        endReason:        reason,
      });
    } catch (err) {
      console.error('[followup] event POST failed:', err);
      if (isMountedRef.current) setPhase(PHASE.ENDED);
      // Parent still needs to unlock the next question, otherwise the
      // candidate is stuck forever staring at "Ended".
      onComplete?.({
        stored:           false,
        skipped:          false,
        micGranted:       micGrantedRef.current,
        micDeniedReason:  micDeniedReasonRef.current,
        endReason:        reason,
      });
    }
  }, [testId, questionIdx, transcript, onComplete]);


  /** Backend told us to skip — close immediately, no UI flash, no POST. */
  const finishSkip = useCallback(() => {
    if (completedRef.current) return;
    completedRef.current = true;
    teardown();
    onComplete?.({
      stored: false, skipped: true,
      micGranted: micGrantedRef.current,
      micDeniedReason: micDeniedReasonRef.current,
      endReason: 'flag_disabled',
    });
  }, [onComplete]);


  // ─────────────────────────────────────────────────────────────────────────
  // Teardown — borrowed-from-Impl-2 thorough cleanup so we don't leak
  // mic tracks / hidden audio elements across multiple coding questions.
  // ─────────────────────────────────────────────────────────────────────────

  const teardown = useCallback(() => {
    try {
      if (dcRef.current) { try { dcRef.current.close(); } catch (_) {} dcRef.current = null; }
      if (pcRef.current) {
        try {
          pcRef.current.getSenders().forEach((s) => { try { s.track?.stop(); } catch (_) {} });
          pcRef.current.close();
        } catch (_) {}
        pcRef.current = null;
      }
      if (audioElRef.current) {
        try {
          audioElRef.current.pause();
          audioElRef.current.srcObject = null;
          audioElRef.current.remove();
        } catch (_) {}
        audioElRef.current = null;
      }
      if (localStreamRef.current) {
        try {
          localStreamRef.current.getTracks().forEach((t) => t.stop());
        } catch (_) {}
        localStreamRef.current = null;
      }
    } catch (e) {
      console.warn('[followup] teardown error', e);
    }
  }, []);


  // ─────────────────────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────────────────────

  if (!open) return null;

  // Skipped path: render NOTHING — we already called onComplete.
  if (phase === PHASE.SKIPPED) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-emerald-50 flex items-center justify-center text-emerald-600">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-slate-900">
              Code Verification — Question {questionIdx + 1} of {totalQuestions}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Quick voice check on your code before the next question.
            </p>
          </div>
          {phase === PHASE.ACTIVE && (
            <div className="flex items-center gap-1.5 text-xs font-medium text-slate-700">
              <Clock className="w-3.5 h-3.5" />
              {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
            </div>
          )}
        </div>

        {/* Body — phase router */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {phase === PHASE.REQUESTING_MIC && (
            <div className="text-center py-10">
              <Mic className="w-10 h-10 mx-auto text-slate-400 mb-3 animate-pulse" />
              <p className="text-sm text-slate-700">Requesting microphone access…</p>
              <p className="text-xs text-slate-500 mt-2">Click <strong>Allow</strong> in the browser prompt.</p>
            </div>
          )}

          {phase === PHASE.MIC_DENIED && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <MicOff className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-900">Microphone required</h3>
                  <p className="text-sm text-red-800 mt-1 leading-relaxed">{errorMessage}</p>
                  <button
                    type="button"
                    onClick={requestMicThenStart}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}

          {phase === PHASE.FETCHING_SESSION && (
            <div className="text-center py-10">
              <Loader2 className="w-10 h-10 mx-auto text-emerald-500 animate-spin mb-3" />
              <p className="text-sm text-slate-700">Setting up the verification call…</p>
            </div>
          )}

          {phase === PHASE.CONNECTING && (
            <div className="text-center py-10">
              <Loader2 className="w-10 h-10 mx-auto text-emerald-500 animate-spin mb-3" />
              <p className="text-sm text-slate-700">Connecting to the AI interviewer…</p>
            </div>
          )}

          {(phase === PHASE.ACTIVE || phase === PHASE.ENDED) && (
            <>
              {/* Code reference panel */}
              <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Your code</p>
                <pre className="text-xs text-slate-700 font-mono leading-relaxed whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {candidateCode}
                </pre>
              </div>

              {/* AI status pulse */}
              <div className={`flex items-center justify-center gap-3 py-4 rounded-md border-2 ${
                aiSpeaking ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 bg-white'
              }`}>
                <div className={`w-3 h-3 rounded-full ${aiSpeaking ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
                <span className="text-sm font-medium text-slate-700">
                  {aiSpeaking ? 'AI is speaking…' : 'Listening — speak naturally.'}
                </span>
              </div>

              {/* Live transcript */}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1.5">Live transcript</p>
                <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2 min-h-[120px] max-h-[260px] overflow-y-auto text-sm">
                  {transcript.length === 0 ? (
                    <p className="text-slate-400 italic text-xs">Waiting for the AI to speak…</p>
                  ) : (
                    transcript.map((line, idx) => (
                      <div key={idx} className={line.role === 'assistant' ? 'text-slate-700' : 'text-slate-900'}>
                        <span className={`text-[10px] uppercase tracking-wide font-semibold mr-2 ${
                          line.role === 'assistant' ? 'text-emerald-600' : 'text-blue-600'
                        }`}>{line.role === 'assistant' ? 'AI' : 'You'}</span>
                        {line.text}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {phase === PHASE.ENDED && (
                <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
                  <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                  Thanks. Moving to the next question…
                </div>
              )}
            </>
          )}

          {phase === PHASE.ERROR && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-amber-900">Verification couldn't run</h3>
                  <p className="text-sm text-amber-800 mt-1 leading-relaxed">{errorMessage}</p>
                  <button
                    type="button"
                    onClick={() => finish('error')}
                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-md hover:bg-amber-700"
                  >
                    <X className="w-3.5 h-3.5" />
                    Close and continue
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer hint — no Close button while mic-denied (HARD GATE).
            Visible only once the AI is done or on the error fallback. */}
        {phase === PHASE.ACTIVE && (
          <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 text-[11px] text-slate-500">
            This call lasts up to 3 minutes. The AI will end it automatically once it has enough information.
          </div>
        )}

      </div>
    </div>
  );
}
